import type { LegacyUserDetail } from '../../services/legacyUserManager';
import type { LegacyMigratePlanProfile } from '../../services/legacyUserManager';

const str = (value: unknown): string | null =>
  value == null || value === '' ? null : String(value).trim() || null;

const intOrNull = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

const isPlaceholderDisplayName = (value: string | null | undefined): boolean => {
  if (!value) return true;
  const s = value.trim();
  if (!s) return true;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    s
  );
};

const normalizePersonName = (value: string | null | undefined): string | null => {
  const s = str(value);
  if (!s || isPlaceholderDisplayName(s)) return null;
  return s
    .split(/(\s+)/)
    .map((part) => {
      if (/^\s+$/.test(part)) return part;
      const lower = part.toLocaleLowerCase('it-IT');
      return lower.charAt(0).toLocaleUpperCase('it-IT') + lower.slice(1);
    })
    .join('');
};

const normalizeFiscalCode = (value: string | null | undefined): string | null => {
  const s = str(value);
  if (!s) return null;
  return s.toLocaleUpperCase('it-IT');
};

const displayName = (value: unknown): string | null =>
  normalizePersonName(str(value));

const earliestDate = (
  ...values: Array<string | Date | null | undefined>
): string | null => {
  let best: Date | null = null;
  let bestRaw: string | null = null;
  for (const value of values) {
    if (value == null || value === '') continue;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) continue;
    if (!best || d.getTime() < best.getTime()) {
      best = d;
      bestRaw =
        typeof value === 'string' ? value : new Date(value).toISOString();
    }
  }
  return bestRaw;
};

/**
 * Mirror of services `buildUserProfileDraft` — used by preview UI when
 * staging dry-run omits `plan.profile` (or to fill gaps from vapor detail / dump).
 */
export const buildProfileFromLegacyDetail = (
  detail: LegacyUserDetail
): LegacyMigratePlanProfile => {
  const user = detail.user ?? {};
  const info = detail.user_info ?? {};
  const bes = detail.bes_users ?? {};
  const customer = Array.isArray(detail.customer_data)
    ? detail.customer_data[0] ?? {}
    : {};
  const professional = Array.isArray(detail.professionals)
    ? detail.professionals[0] ?? {}
    : {};

  const firstName =
    displayName(bes.first_name) ||
    displayName(info.name) ||
    displayName(professional.name) ||
    null;
  const lastName =
    displayName(bes.last_name) ||
    displayName(info.surname) ||
    displayName(professional.surname) ||
    null;
  const fullName =
    displayName(bes.full_name) ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    displayName(user.name) ||
    null;

  const customerAddress = [
    str(customer.address),
    str(customer.zip_code),
    str(customer.city),
    str(customer.state),
    str(customer.country),
  ]
    .filter(Boolean)
    .join(', ');

  const address =
    customerAddress ||
    str(professional.address) ||
    str(bes.address) ||
    null;

  return {
    first_name: firstName,
    last_name: lastName,
    full_name: fullName,
    type: str(bes.type) || str(user.type) || 'professional',
    title: str(bes.title) || str(professional.title) || str(info.title) || null,
    role: str(info.role) || str(bes.role) || null,
    profession: intOrNull(info.profession) ?? intOrNull(bes.profession),
    disciple: intOrNull(info.disciple) ?? intOrNull(bes.disciple),
    contract: str(info.contract) || str(bes.contract) || null,
    fiscal_code: normalizeFiscalCode(
      str(customer.tax_code) ||
        str(customer.fiscal_code) ||
        str(info.fiscal_code) ||
        str(bes.fiscal_code) ||
        null
    ),
    order_of_psychologists:
      str(user.order_of_psychologists) ||
      str(bes.order_of_psychologists) ||
      null,
    order_number: str(user.order_number) || str(bes.order_number) || null,
    region: str(bes.region) || str(professional.region) || str(user.region) || null,
    address,
    website:
      str(bes.website) || str(professional.website) || str(user.website) || null,
    is_public: intOrNull(bes.is_public) ?? intOrNull(user.is_public),
    profile_photo_path:
      str(user.profile_photo_path) || str(bes.profile_photo_path) || null,
    stripe_id: str(user.stripe_id) || str(bes.stripe_id) || null,
    vat_number:
      str(customer.VAT_number) ||
      str(customer.vat_number) ||
      str(bes.vat_number) ||
      null,
    sdi_number:
      str(customer.SDI) || str(customer.sdi_number) || str(bes.sdi_number) || null,
    created_at: earliestDate(
      user.created_at as string | Date | null,
      info.created_at as string | Date | null,
      bes.created_at as string | Date | null
    ),
  };
};

/** Prefer non-empty detail fields when dry-run profile is missing/sparse. */
export const mergeMigrationProfiles = (
  fromPlan: LegacyMigratePlanProfile | null | undefined,
  fromDetail: LegacyMigratePlanProfile
): LegacyMigratePlanProfile => {
  const base = fromPlan ?? ({} as LegacyMigratePlanProfile);
  const pick = <K extends keyof LegacyMigratePlanProfile>(
    key: K
  ): LegacyMigratePlanProfile[K] => {
    const a = base[key];
    const b = fromDetail[key];
    if (a != null && a !== '') return a;
    return b;
  };

  return {
    first_name: pick('first_name'),
    last_name: pick('last_name'),
    full_name: pick('full_name'),
    type: pick('type') || 'professional',
    title: pick('title'),
    role: pick('role'),
    profession: pick('profession'),
    disciple: pick('disciple'),
    contract: pick('contract'),
    fiscal_code: pick('fiscal_code'),
    order_of_psychologists: pick('order_of_psychologists'),
    order_number: pick('order_number'),
    region: pick('region'),
    address: pick('address'),
    website: pick('website'),
    is_public: pick('is_public'),
    profile_photo_path: pick('profile_photo_path'),
    stripe_id: pick('stripe_id'),
    vat_number: pick('vat_number'),
    sdi_number: pick('sdi_number'),
    created_at: pick('created_at'),
  };
};
